import React, { useState, useContext } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import ProgressBar from "@ramonak/react-progress-bar";
import { useNavigate } from "react-router-dom";
import { Bars } from "react-loader-spinner";
import InfoTextCard from "../../components/common/InfoCard/InfoCard";
import { GloblInfo } from "../../App"; // Global context for user session

const FileUploadAll = () => {
  const { userSession } = useContext(GloblInfo); // User session from global context
  const [file, setFile] = useState(null);
  const [fileTitle, setFileTitle] = useState("");
  const [fileDescription, setFileDescription] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubCategory] = useState("");
  const [customCategory, setCustomCategory] = useState(""); // For user-defined category
  const [progressPercent, setProgressPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [classGroup, setClassGroup] = useState(""); // Selected class
  const [subject, setSubject] = useState(""); // Selected subject
  const [semester, setSemester] = useState("");
  const [course, setCourse] = useState("");
  const [year, setYear] = useState("");
  const [college, setCollege] = useState("");
  const navigate = useNavigate();
  const [isDragActive, setIsDragActive] = useState(false);

  // Subject options based on selected class
  const subjects = {
    "Class 8": [
      "Hindi",
      "English",
      "Math",
      "Physics",
      "Science",
      "Chemistry",
      "Biology",
      "History",
      "Geography",
      "Economics",
      "Computer",
      "General Knowledge",
      "Art & Craft",
      "other",
    ],
    "Class 9": [
      "Hindi",
      "English",
      "Math",
      "Physics",
      "Science",
      "Chemistry",
      "Biology",
      "History",
      "Geography",
      "Economics",
      "Computer Applications",
      "Art",
      "Physical Education",
      "other",
    ],
    "Class 10": [
      "Hindi",
      "English",
      "Math",
      "Physics",
      "Science",
      "Chemistry",
      "Biology",
      "History",
      "Geography",
      "Economics",
      "Computer Applications",
      "Art",
      "Physical Education",
      "other",
    ],
    "Class 11 (Science)": [
      "Physics",
      "Chemistry",
      "Biology",
      "Science",
      "Math",
      "English",
      "Computer Science",
      "Physical Education",
      "Environmental Studies",
      "other",
    ],
    "Class 11 (Commerce)": [
      "Accountancy",
      "Business Studies",
      "Economics",
      "Math",
      "English",
      "Computer Science",
      "other",
    ],
    "Class 11 (Arts)": [
      "History",
      "Geography",
      "Political Science",
      "Economics",
      "English",
      "Hindi",
      "Psychology",
      "Sociology",
      "Philosophy",
      "Fine Arts",
      "other",
    ],
    "Class 12 (Science)": [
      "Physics",
      "Chemistry",
      "Biology",
      "Math",
      "English",
      "Computer Science",
      "Physical Education",
      "Environmental Studies",
      "other",
    ],
    "Class 12 (Commerce)": [
      "Accountancy",
      "Business Studies",
      "Economics",
      "Math",
      "English",
      "Computer Science",
      "other",
    ],
    "Class 12 (Arts)": [
      "History",
      "Geography",
      "Political Science",
      "Economics",
      "English",
      "Hindi",
      "Psychology",
      "Sociology",
      "Philosophy",
      "Fine Arts",
      "other",
    ],
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };
  
  const handleDragLeave = () => {
    setIsDragActive(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };
  

  // Handle file input change
  const handleFileChange = (e) => setFile(e.target.files[0]);

  // Add a tag (max 5 tags)
  const handleAddTag = () => {
    if (tagInput && tags.length < 5) {
      setTags([...tags, tagInput]);
      setTagInput("");
    }
  };

  // Remove a tag
  const handleRemoveTag = (tagToRemove) =>
    setTags(tags.filter((tag) => tag !== tagToRemove));

  const handleUpload = async (e) => {
    e.preventDefault();

    // Validate fields
    if (
      !file ||
      !fileTitle ||
      !fileDescription ||
      (!category && !customCategory) ||
      (category === "School" && (!classGroup || !subject))
    ) {
      toast.error("Please fill all the fields.");
      return;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", fileTitle);
    formData.append("description", fileDescription);
    formData.append("tags", tags.join(",")); // Tags as a comma-separated string
    formData.append(
      "category",
      category === "Other" ? customCategory : category
    ); // Chosen or custom category
    formData.append("subcategory", subcategory);
    formData.append("classGroup", classGroup || "");
    formData.append("subject", subject || "");
    formData.append("username", userSession.username); // Include username from session
    formData.append("semester", semester || ""); // Add semester
    formData.append("course", course || ""); // Add course
    formData.append("year", year || ""); // Add year
    formData.append("college", college || ""); // Add college

    // Debug: Log the formData content
   
    // for (let pair of formData.entries()) {
    //   console.log(`${pair[0]}: ${pair[1]}`);
    // }

    try {
      setLoading(true);
      

      const response = await axios.post(
        process.env.REACT_APP_UPLOAD_OTHERS_PDF,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true, // Include cookies for CORS requests
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
           
            setProgressPercent(percentCompleted); // Update progress bar
          },
        }
      );

      // Debug: Log response
  

      const result = response.data;

      // Check and handle response
      if (result.message) {
        toast.success(result.message);
        

        if (
          result.message === "File and first page image uploaded successfully."
        ) {
          navigate(`/profile/${userSession.username}`); // Navigate to user profile
          resetForm(); // Clear form inputs
        }
      } else {
        toast.error("Unexpected response from the server.");
        console.error("Unexpected response data:", result);
      }
    } catch (error) {
      // Debug: Log error
      console.error("Error during file upload:", error);

      // Handle specific error responses
      if (error.response) {
        console.error("Server responded with error:", error.response.data);
        toast.error(
          error.response.data?.error || "An error occurred on the server."
        );
      } else if (error.request) {
        console.error("No response received:", error.request);
        toast.error("No response received from the server.");
      } else {
        console.error("Error setting up request:", error.message);
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
   
    }
  };

  // Reset form fields after successful upload
  const resetForm = () => {
    setFile(null);
    setFileTitle("");
    setFileDescription("");
    setTags([]);
    setCategory("");
    setSubCategory("");
    setCustomCategory("");
    setClassGroup("");
    setSubject("");
    setProgressPercent(0);
  };

  return (
    <div className="file-upload-container">
      <div className="file-upload-card">
        <h3>Upload Files</h3>

        <div
  className={`file-dropbox ${isDragActive ? "drag-active" : ""}`}
  onDragOver={handleDragOver}
  onDragEnter={handleDragOver}
  onDragLeave={handleDragLeave}
  onDrop={handleDrop}
>
  {file ? (
    <header>
      <h4>File Selected: {file.name}</h4>
    </header>
  ) : (
    <>
      <header>
        <h4>Drag & Drop file here or click to select</h4>
      </header>
      <input
        type="file"
        accept=".pdf"
        onChange={handleFileChange}
        id="fileInputID"
        style={{ display: "none" }}
      />
      <label htmlFor="fileInputID" className="upload-btn">
        Choose File
      </label>
    </>
  )}
</div>

        <div className="form-input-section">
          <div className="form-input-group">
            <label htmlFor="fileTitleInput">File Title</label>
            <input
              type="text"
              id="fileTitleInput"
              placeholder="File Title"
              onChange={(e) => setFileTitle(e.target.value)}
              value={fileTitle}
              required
            />
          </div>
          <div className="form-input-group">
            <label htmlFor="fileDescriptionInput">File Description</label>
            <textarea
              id="fileDescriptionInput"
              placeholder="File Description"
              onChange={(e) => setFileDescription(e.target.value)}
              value={fileDescription}
              required
            />
          </div>

          {/* Category Input */}
          <div className="form-input-group">
            <label htmlFor="categorySelect">Select Category</label>
            <select
              id="categorySelect"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">Select Category</option>
              <option value="School">School</option>
              <option value="College">College</option>
              <option value="University">University</option>
              <option value="Banking">Banking</option>
              <option value="Business">Business</option>
              <option value="Competition">Competition Exam</option>
              <option value="Legal">Legal</option>
              <option value="Engineering">Engineering</option>
              <option value="Medical">Medical</option>
              <option value="Programming">Programming</option>
              <option value="Artificial Intelligence">
                Artificial Intelligence
              </option>
              <option value="Health & Fitness">Health & Fitness</option>
              <option value="History">History</option>
              <option value="Science">Science</option>
              <option value="Travel">Travel & Tourism</option>
              <option value="Music">Music</option>
              <option value="Religious Studies">Religious Studies</option>
              <option value="Personal Finance">Personal Finance</option>
              <option value="Environmental Studies">
                Environmental Studies
              </option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Category Input */}
          <div className="form-input-group">
            <label htmlFor="categorySelect">Select Sub Category</label>
            <select
              id="categorySelect"
              value={subcategory}
              onChange={(e) => setSubCategory(e.target.value)}
              required
            >
              <option value="">Select SubCategory</option>
              <option value="PDF">PDF</option>
              <option value="old-paper">Old Paper</option>
              <option value="notes">Notes</option>
              <option value="ebooks">eBooks</option>
              <option value="manuals">Manuals</option>
              <option value="syllabus">Syllabus</option>
              <option value="assignments">Assignments</option>
              <option value="research-papers">Research Papers</option>
              <option value="sample-papers">Sample Papers</option>
              <option value="question-banks">Question Banks</option>
              <option value="practice-tests">Practice Tests</option>
              <option value="tutorials">Tutorials</option>
              <option value="templates">Templates</option>
              <option value="worksheets">Worksheets</option>
            </select>
          </div>

          {/* School-Specific Inputs */}
          {category === "School" && (
            <>
              {/* Class Selection */}
              <div className="form-input-group">
                <label htmlFor="classSelect">Select Class</label>
                <select
                  id="classSelect"
                  value={classGroup}
                  onChange={(e) => setClassGroup(e.target.value)}
                  required
                >
                  <option value="">Select Class</option>
                  {Object.keys(subjects).map((cls) => (
                    <option key={cls} value={cls}>
                      {cls}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject Selection */}
              {classGroup && (
                <div className="form-input-group">
                  <label htmlFor="subjectSelect">Select Subject</label>
                  <select
                    id="subjectSelect"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  >
                    <option value="">Select Subject</option>
                    {subjects[classGroup].map((sub) => (
                      <option key={sub} value={sub}>
                        {sub}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* Tags Input */}
          <div className="form-input-group tag-input-group">
            <label htmlFor="tagInput">Add Tag (Max 5)</label>
            <InfoTextCard tags={tags} onRemoveTag={handleRemoveTag} />
            <span style={{ display: "flex", gap: "20px" }}>
            <input
  type="text"
  value={tagInput}
  onChange={(e) => setTagInput(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission if inside a form
      handleAddTag();
    }
  }}
  placeholder="Enter tag"
/>
              <button className="upload-btn" onClick={handleAddTag}>
                Add
              </button>
            </span>
          </div>

          {/* Progress Bar and Upload Button */}
          <div className="form-input-group tag-input-group">
            <ProgressBar
              completed={progressPercent}
              height="15px"
              bgColor="#005af0"
            />
            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? "Uploading..." : "Upload File"}
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-bar">
          <Bars color="#005af0" />
        </div>
      )}
    </div>
  );
};

export default FileUploadAll;
